import { App, Button, Input, InputNumber, Modal, Select, Tooltip } from 'antd';
import { Link2, Plus, SlidersHorizontal, Trash2, X } from 'lucide-react';
import { useCallback, useRef, useState } from 'react';
import type { KeyboardEvent, ClipboardEvent } from 'react';
import type { OrgUnitSummaryDto } from '@pams/shared';
import {
  applyPaste,
  balanceTone,
  parseClipboardMatrix,
  remainingByColumn,
  splitEqually,
  type BalanceTone,
  type ForceGrid as ForceGridModel,
  type GridColumn,
  type GridColumnKind,
} from '@/lib/forceGrid';
import { cn } from '@/lib/utils';
import { SURFACES } from '@/lib/surfaces';
import { TONE_VAR, type StatusTone } from '@/theme/statusTone';
import { FORCE_COPY } from './copy';
import { SourceCell } from './SourceCell';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  THE FORCE GRID — the screen that decides whether this product gets used.
 *
 *  PA_REQUIREMENTS §6.1 Step 3 draws it: post types across the top, TOTAL REQUIRED typed
 *  straight into the first row, then one "+ Add source" row per division, and a REMAINING
 *  row underneath that reconciles the two.
 *
 *  ⚠️ THE REMAINING ROW IS THE ENTIRE INTERACTION MODEL, AND IT IS THE ONLY INSTRUCTION.
 *
 *  There is no validation modal and no error toast on this screen — not when a column is
 *  short, and not when it is over-allocated. The operator types numbers until the row goes
 *  green. A layman can be handed this table with no training because the feedback is
 *  continuous and in the same place as the work, which a modal is not.
 *
 *  The arithmetic lives in `web/src/lib/forceGrid.ts` and is unit-tested without rendering;
 *  this file is the surface. Anything here that computes a balance is a bug.
 *
 *  ─── ⚠️ TWO THINGS THAT LOOK LIKE POLISH AND ARE NOT ─────────────────────────────
 *
 *  `keyboard={false}` on every `InputNumber`. AntD's number input binds ArrowUp and
 *  ArrowDown to step the value, so a grid that also uses arrows to move between cells
 *  fights it: the operator presses Down expecting the next division and the count silently
 *  becomes 3. With `keyboard={false}` the component stops claiming them and Enter still
 *  flushes the value. Measured against `rc-input-number`, not assumed.
 *
 *  Paste is bound to the TABLE, not to a cell. A spreadsheet block is pasted with one cell
 *  focused and lands across many, so the handler has to see the event where the whole
 *  matrix can be addressed — and `parseClipboardMatrix` returns `null` for a non-numeric
 *  cell so pasting a block whose first column is post-type NAMES does not wipe the numbers
 *  beside them.
 *
 *  ⚠️ NOTHING IN THIS FILE KNOWS COMMAND EXISTS. No prop, no import, no branch. FR-5.2.6b
 *  closes OD-9 because everybody assumes command counts toward force; here that is held by
 *  there being no argument through which one could arrive.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

/**
 * ⚠️ [C5c §2] A COLUMN ANOTHER DUTY POINT ASKS FOR ON THIS ONE'S BEHALF.
 *
 * It is NOT a grid column: this card carries no requirement line for it (that is the whole of
 * "asked for once"), so it has no `lineIndex`, no total, no sources and nothing to type. The grid
 * renders it as a header and a dash in every band so the card still shows the column exists —
 * brief 2c — and so a card added after sharing began shows it too.
 */
export type CoveredColumn = {
  lineKey: string;
  kind: GridColumnKind;
  label: string;
  fullLabel: string;
  /** `DP2` — the anchor's ordinal on this order, the same one the record and the sheet print. */
  anchorLabel: string;
  /** What the anchor asks for, for the dash's accessible name. */
  anchorCount: number;
};

export type ForceGridProps = {
  grid: ForceGridModel;
  onChange: (next: ForceGridModel) => void;
  /**
   * FR-5.2.6c — a one-off column for THIS order only. Separate from `onChange` because it
   * adds a requirement LINE, which the grid model derives from rather than owns.
   */
  onAddAdHoc?: (label: string) => void;
  /**
   * ⚠️ [D4.3] REMOVING A ONE-OFF COLUMN, AND ONLY A ONE-OFF COLUMN.
   *
   * A policy column may not be removed here — the columns ARE the policy, and dropping one
   * on a single order is what `policyColumns.ts` calls a fork. A one-off row belongs to this
   * duty point and nothing else, so it is the one column an operator may take back.
   */
  onRemoveAdHoc?: ((lineIndex: number) => void) | undefined;
  /**
   * ⚠️ [amended C1c §4a] WHICH POSTS MAY FILL A ONE-OFF COLUMN — AND ONLY A ONE-OFF COLUMN.
   *
   * A one-off row carries no post type, so the eligibility search places NO rank predicate on
   * it at all: any officer in the addressed unit may be named. That is the right DEFAULT and
   * the wrong answer for "Bomb squad", so an operator may narrow it to the ranks a set of posts
   * accepts. `[]` means ANY, which is what every column meant before C1c.
   *
   * ⚠️ IT NEVER TOUCHES A POLICY COLUMN, and the control is ABSENT rather than disabled there
   * — [D4.3]'s rule on the sibling Remove: "a disabled button invites the question 'how do I
   * enable it?' about something that must stay impossible."
   */
  onRestrictAdHoc?: ((lineIndex: number, postTypeIds: readonly string[]) => void) | undefined;
  /** The post types an operator may narrow a one-off column to. Empty hides the control. */
  postTypeOptions?: readonly { id: string; name: string }[];
  /** Resolved names for the source rows. Missing ids render as the id, never blank. */
  orgUnits: ReadonlyMap<string, OrgUnitSummaryDto>;
  readOnly?: boolean;
  /**
   * ⚠️ [amended D4] WHICH CARD THIS GRID BELONGS TO, FOLDED INTO EVERY ACCESSIBLE NAME.
   *
   * Two duty points on one policy render two grids with identical columns, so without this
   * there are two spinbuttons called "Total required, SI/Sgt" on one page — a real defect
   * for a screen reader, not merely a test problem. Defaulted, so `StandingOrderBuilder`
   * and `ForceGrid.test.tsx` are untouched.
   */
  context?: string;
  /**
   * ⚠️ [amended D4.1] DROPS THE GRID'S OWN CARD, FOR A CALLER THAT IS ALREADY A SURFACE.
   *
   * Inside a duty point the grid is one labelled block among five, and D4.1 §2 asks for
   * those to be "separated by space and hairlines — no boxes of their own". The header's
   * rule stays, because it is what separates the controls from the table; what goes is the
   * four-sided box around the whole thing. `StandingOrderBuilder` passes nothing and keeps
   * its card, which is right there — the grid is the only thing in that section.
   */
  flush?: boolean;
  /**
   * ─────────────────────────────────────────────────────────────────────────────────
   *  ⚠️ [C5c §2] SHARED COLUMNS — FOUR OPTIONAL PROPS, SO `StandingOrderBuilder` IS UNTOUCHED.
   *
   *  `coveredColumns`     the columns another card answers for: a header and a dash per band,
   *                       never an input, never registered for the arrow keys and outside paste
   *                       (`applyPaste` addresses grid columns, and these are not in the grid).
   *  `sharedLineIndexes`  this card's own lines that cover every card — captioned in the header.
   *  `onToggleShare`      the per-column toggle; ABSENT (not disabled) where sharing is not
   *                       offered, [D4.3]'s rule for a control that must stay impossible.
   *  `refuseAdHoc`        a sentence when a new one-off column is already covered here.
   *
   *  ⚠️ NO TOOLTIP. The toggle names itself in `aria-label`, and a new `Tooltip` would move
   *  `census.spec.ts`' exempted count for this file.
   * ─────────────────────────────────────────────────────────────────────────────────
   */
  coveredColumns?: readonly CoveredColumn[];
  sharedLineIndexes?: ReadonlySet<number>;
  onToggleShare?: ((lineIndex: number) => void) | undefined;
  refuseAdHoc?: ((label: string) => string | undefined) | undefined;
  'data-testid'?: string;
};

const NO_SHARED_LINES: ReadonlySet<number> = new Set();

/**
 * ⚠️ [D4.5 §1e] ONE MAP, SO A STATE CANNOT GET A TONE WITHOUT ITS TINT.
 *
 * It was two — `BALANCE_TONE` beside a `TONE_CLASS` — and the pair is exactly how a fifth
 * state would arrive coloured in one of them and not the other. The tone drives the text
 * (through `TONE_VAR`, a VARIABLE reference, so a `ThemeScope` renders it correctly) and
 * the tint drives the cell wash. They are declared together because they mean one thing.
 *
 * ⚠️ `short` WAS `neutral`. Three states now carry colour where two did: balanced ·
 * still-to-source · over-allocated, plus an untinted neutral for a column nobody has asked
 * for anything in. That last one is not "balanced": zero of zero is not an achievement, and
 * a green cell for an untouched column would tell an operator their work was finished
 * before they started — `balanceTone`'s own argument, which is why it has four states and
 * this map has four rows.
 *
 * ⚠️ A TAILWIND OPACITY MODIFIER, NOT AN INLINE `color-mix`, AND THAT IS MEASURED RATHER
 * THAN PREFERRED. `styles/index.css` maps `--color-success: var(--success)` under
 * `@theme inline`, so `bg-success/15` emits a mix against the same custom property the text
 * reads — one indirection, no `dark:`, and no colour value outside `tokens.ts`. What decides
 * it is TESTABILITY: jsdom's cssstyle silently DROPS a `color-mix(...)` declaration
 * (`StatusPill.tsx`'s header records the same trap), so an inline tint would be assertable
 * only in a browser, while a class can be read.
 *
 * ⚠️ NO `font-semibold` HERE. The balance `<tr>` below carries it and font-weight inherits,
 * so the old `TONE_CLASS` was contributing nothing at all — "bold success text" already
 * shipped, via the row. Adding a per-tone weight would be a second no-op.
 */
const BALANCE: Record<BalanceTone, { tone: StatusTone; tint: string }> = {
  balanced: { tone: 'success', tint: 'bg-success/15' },
  short: { tone: 'warning', tint: 'bg-warning/15' },
  over: { tone: 'danger', tint: 'bg-destructive/15' },
  empty: { tone: 'neutral', tint: '' },
};

/** `-2` reads as an error; `2 over` reads as an instruction. */
const remainingLabel = (remaining: number): string =>
  remaining === 0 ? '0' : remaining < 0 ? `${String(Math.abs(remaining))} over` : String(remaining);

export const ForceGrid = ({
  grid,
  onChange,
  onAddAdHoc,
  onRemoveAdHoc,
  onRestrictAdHoc,
  postTypeOptions = [],
  orgUnits,
  readOnly = false,
  context,
  flush = false,
  coveredColumns = [],
  sharedLineIndexes = NO_SHARED_LINES,
  onToggleShare,
  refuseAdHoc,
  'data-testid': testId,
}: ForceGridProps) => {
  /** ⚠️ Suffixed, never prefixed: the column name stays the first thing read out. */
  const named = (label: string) => (context ? `${label} — ${context}` : label);
  const { modal } = App.useApp();
  const remaining = remainingByColumn(grid);
  const cellRefs = useRef(new Map<string, HTMLElement>());
  const [adHocOpen, setAdHocOpen] = useState(false);
  const [adHocLabel, setAdHocLabel] = useState('');
  /** Which one-off column's restriction is being edited, and the draft selection. */
  const [restricting, setRestricting] = useState<GridColumn | null>(null);
  const [restriction, setRestriction] = useState<string[]>([]);

  const registerCell = useCallback((key: string, node: HTMLElement | null) => {
    if (node) cellRefs.current.set(key, node);
    else cellRefs.current.delete(key);
  }, []);

  const focusCell = useCallback((row: number, column: number) => {
    const node = cellRefs.current.get(`${String(row)}:${String(column)}`);
    node?.focus();
  }, []);

  const setTotal = (lineIndex: number, value: number | null): void =>
    onChange({ ...grid, totals: grid.totals.map((total, index) => (index === lineIndex ? (value ?? 0) : total)) });

  const setCell = (sourceIndex: number, lineIndex: number, value: number | null): void =>
    onChange({
      ...grid,
      cells: grid.cells.map((row, index) =>
        index === sourceIndex ? row.map((cell, column) => (column === lineIndex ? (value ?? 0) : cell)) : row,
      ),
    });

  /*
   * ⚠️ ONE BLANK ROW AT A TIME. A source's identity is `<division>:<unit>`, so two rows with
   * no division chosen are ONE row — the second "Add source" would appear to do nothing.
   * Disabling it says so, rather than leaving the operator clicking a dead button.
   */
  const hasBlankSource = grid.sources.some((source) => source.divisionOrgUnitId.trim().length === 0);

  const addSource = (): void =>
    onChange({
      ...grid,
      sources: [
        ...grid.sources,
        { key: `new:${String(grid.sources.length)}`, divisionOrgUnitId: '', unitOrgUnitId: null },
      ],
      cells: [...grid.cells, grid.totals.map(() => 0)],
    });

  const removeSource = (sourceIndex: number): void =>
    onChange({
      ...grid,
      sources: grid.sources.filter((_source, index) => index !== sourceIndex),
      cells: grid.cells.filter((_row, index) => index !== sourceIndex),
    });

  /**
   * ⚠️ [D4.5 §1b] THE UNIT GOES WITH THE DIVISION, IN THIS SETTER AND NOWHERE ELSE.
   *
   * It used to be two calls from `SourceCell` — `onChangeUnit(undefined)` and then
   * `onChangeDivision(next)`, in one synchronous handler — and BOTH derive their next value
   * from the same `grid` PROP. So the clear landed first and was then overwritten by a grid
   * rebuilt from the unchanged prop, and the stale unit survived. Two files asserted the
   * rule held; neither tested it.
   *
   * ⚠️ AND THE SURVIVING STALE UNIT IS WHAT PUT AN ObjectId ON SCREEN. `unitOptions` keeps a
   * chosen unit only when it is a descendant of the division, so a unit stranded by a
   * division change is in `value` and absent from `options` — and rc-select's last step is
   * `label ?? item.value`. One setter, one write, and the id never arises.
   *
   * ⚠️ THE `=== next` GUARD IS NOT DEFENSIVE. Re-picking the SAME division must not silently
   * drop a unit the operator already chose, and relying on rc-select to fire `onChange` only
   * on an actual change would be relying on a library's behaviour for a correctness rule.
   */
  const setDivision = (sourceIndex: number, divisionOrgUnitId: string | undefined): void => {
    const next = divisionOrgUnitId ?? '';
    onChange({
      ...grid,
      sources: grid.sources.map((source, index) => {
        if (index !== sourceIndex) return source;
        const unitOrgUnitId = source.divisionOrgUnitId === next ? source.unitOrgUnitId : null;
        return { ...source, divisionOrgUnitId: next, unitOrgUnitId, key: `${next}:${unitOrgUnitId ?? ''}` };
      }),
    });
  };

  const setUnit = (sourceIndex: number, unitOrgUnitId: string | undefined): void =>
    onChange({
      ...grid,
      sources: grid.sources.map((source, index) =>
        index === sourceIndex
          ? {
              ...source,
              unitOrgUnitId: unitOrgUnitId ?? null,
              key: `${source.divisionOrgUnitId}:${unitOrgUnitId ?? ''}`,
            }
          : source,
      ),
    });

  /**
   * Arrows move, Enter adds a row.
   *
   * The grid is NOT a roving-tabindex widget like `HierarchyTable`: every cell here is a
   * real input the operator must be able to Tab into, so Tab keeps its native meaning and
   * the arrows are the extra. Taking Tab over would break the one navigation every user
   * already knows.
   */
  const onGridKeyDown = (row: number, column: number) => (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (hasBlankSource) return;
      addSource();
      // The new row is appended, so the equivalent cell on it is where work continues.
      window.setTimeout(() => focusCell(grid.sources.length, column), 0);
      return;
    }
    const step: Record<string, [number, number]> = {
      ArrowUp: [-1, 0],
      ArrowDown: [1, 0],
      ArrowLeft: [0, -1],
      ArrowRight: [0, 1],
    };
    const move = step[event.key];
    if (!move) return;
    event.preventDefault();
    focusCell(row + move[0], column + move[1]);
  };

  const onGridPaste = (event: ClipboardEvent<HTMLElement>): void => {
    const text = event.clipboardData.getData('text/plain');
    if (!text.includes('\t') && !text.includes('\n')) return;
    event.preventDefault();

    const focusedKey = [...cellRefs.current.entries()].find(([, node]) => node.contains(document.activeElement))?.[0];
    const [row, column] = (focusedKey ?? '-1:0').split(':').map(Number);
    const result = applyPaste(grid, { row: row ?? -1, column: column ?? 0 }, parseClipboardMatrix(text));
    onChange(result.grid);
  };

  /**
   * ⚠️ [FIX-1] EVERY COLUMN GETS ONE OF THESE, POSTS AND VEHICLES ALIKE.
   *
   * `label` is already `shortName || name` (posts) or `code || name` (vehicles) in
   * `requirementColumns`, and an unresolved id becomes `—` in `toGrid` rather than an empty
   * string — so a blank header is not reachable from the data. It was reachable from the
   * markup, which is what changed. `title` carries the unabbreviated name, so the
   * `truncate` never hides a name with no way to read it.
   */
  /*
   * ⚠️ [D4.3] WHAT A ONE-OFF COLUMN WOULD COST TO REMOVE, ASKED OF THE GRID ITSELF.
   *
   * The total and every source cell for that column. It is a QUESTION, not a running
   * figure: nothing renders it, and it exists only so the confirmation can say what is
   * lost rather than asking a bare "are you sure?".
   */
  const filledOn = (lineIndex: number): boolean =>
    (grid.totals[lineIndex] ?? 0) > 0 || grid.cells.some((row) => (row[lineIndex] ?? 0) > 0);

  const removeAdHocColumn = (column: GridColumn): void => {
    if (!onRemoveAdHoc) return;
    if (!filledOn(column.lineIndex)) {
      onRemoveAdHoc(column.lineIndex);
      return;
    }
    modal.confirm({
      title: FORCE_COPY.removeAdHocTitle(column.label),
      content: FORCE_COPY.removeAdHocBody(grid.totals[column.lineIndex] ?? 0),
      okText: 'Remove',
      okButtonProps: { danger: true },
      onOk: () => {
        onRemoveAdHoc(column.lineIndex);
      },
    });
  };

  const columnHeader = (column: GridColumn, options?: { rowSpan?: number }) => (
    <th
      key={column.lineIndex}
      scope="col"
      rowSpan={options?.rowSpan}
      title={column.fullLabel}
      className="max-w-24 px-1 py-2 align-bottom text-center text-xs font-medium"
    >
      <span className="block truncate">{column.label}</span>
      {sharedLineIndexes.has(column.lineIndex) && (
        <span
          className="block text-[10px] font-normal text-muted-foreground"
          data-testid={`shared-column-${String(column.lineIndex)}`}
        >
          {FORCE_COPY.sharedHere}
        </span>
      )}
      {/*
        ⚠️ A PLAIN `<button>`, NOT AN antd `Button` — AND THAT IS MEASURED, NOT PREFERRED. One antd
        `Button` per column header took `ArrangementBuilder.test.tsx`'s two-card reorder case from
        ~20s to ~148s: every role query over the card slowed five-fold (48s against 10s per
        `getByRole`), while a plain button with the same attributes left it at the baseline. The
        toggle needs none of what antd adds — no loading state, no wave, no icon slot — and the
        remove and restrict controls beside it render only on a one-off column, which is rare.
      */}
      {onToggleShare && (
        <button
          type="button"
          disabled={readOnly}
          className={cn(
            'mt-0.5 inline-flex h-6 items-center gap-1 whitespace-nowrap rounded px-1.5 text-xs transition-colors hover:bg-hover',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50',
            sharedLineIndexes.has(column.lineIndex) ? 'bg-active text-foreground' : 'text-muted-foreground',
          )}
          aria-pressed={sharedLineIndexes.has(column.lineIndex)}
          aria-label={named(
            sharedLineIndexes.has(column.lineIndex)
              ? FORCE_COPY.stopSharing(column.label)
              : FORCE_COPY.shareColumn(column.label),
          )}
          /* A native `title`, never an antd `Tooltip` — one per column header is the cost measured above. */
          title={
            sharedLineIndexes.has(column.lineIndex)
              ? FORCE_COPY.stopSharing(column.label)
              : FORCE_COPY.shareColumn(column.label)
          }
          data-testid={`share-column-${String(column.lineIndex)}`}
          onClick={() => {
            onToggleShare(column.lineIndex);
          }}
        >
          <Link2 className="size-3" aria-hidden />
          {/* [C5c follow-up] The words, not only the icon — pressed state stays `aria-pressed`. */}
          <span>{FORCE_COPY.linkAll}</span>
        </button>
      )}
      {/*
        ⚠️ [D4.3] ONLY ON A ONE-OFF, and only when the caller can take it back. A policy
        column has no control here at all — not a disabled one, because a disabled button
        invites the question "how do I enable it?" about something that must stay impossible.
      */}
      {/*
      ⚠️ [C1c §4a] THE FIRST PER-COLUMN AFFORDANCE IN THIS HEADER, and it renders for a
      one-off column only — for the same reason the Remove X beside it does.
    */}
      {column.kind === 'adhoc' && onRestrictAdHoc && postTypeOptions.length > 0 && (
        <Button
          type="text"
          size="small"
          disabled={readOnly}
          className="mt-0.5"
          aria-label={named(FORCE_COPY.restrictAdHocLabel(column.label))}
          data-testid={`restrict-ad-hoc-${String(column.lineIndex)}`}
          onClick={() => {
            setRestricting(column);
            setRestriction([...column.restrictToPostTypeIds]);
          }}
        >
          <SlidersHorizontal className="size-3" aria-hidden />
        </Button>
      )}
      {column.kind === 'adhoc' && onRemoveAdHoc && (
        <Button
          type="text"
          size="small"
          disabled={readOnly}
          className="mt-0.5"
          aria-label={named(FORCE_COPY.removeAdHocLabel(column.label))}
          data-testid={`remove-ad-hoc-${String(column.lineIndex)}`}
          onClick={() => {
            removeAdHocColumn(column);
          }}
        >
          <X className="size-3" aria-hidden />
        </Button>
      )}
    </th>
  );

  const coveredHeader = (column: CoveredColumn, at: number) => (
    <th
      key={column.lineKey}
      scope="col"
      title={column.fullLabel}
      className={cn(
        'max-w-24 px-1 py-2 align-bottom text-center text-xs font-medium',
        at === 0 && 'border-l border-border',
      )}
      data-testid={`covered-header-${column.lineKey}`}
    >
      <span className="block truncate">{column.label}</span>
      <span className="block text-[10px] font-normal text-muted-foreground">
        {FORCE_COPY.sharedFrom(column.anchorLabel)}
      </span>
    </th>
  );

  /**
   * ⚠️ A DASH, NOT AN INPUT, IN EVERY BAND. The column is ASKED FOR on another card, so there is
   * nothing to type here and nothing to source; an input would be a second place to type the
   * number the sharing exists to ask for once. The accessible name says where it is asked.
   */
  const coveredCells = (band: string, cellClass: string) =>
    coveredColumns.map((column, at) => (
      <td
        key={column.lineKey}
        aria-label={named(FORCE_COPY.coveredCell(column.fullLabel, column.anchorLabel, column.anchorCount))}
        data-testid={`covered-${band}${column.lineKey}`}
        className={cn(cellClass, 'text-center text-subtle', at === 0 && 'border-l border-border')}
      >
        —
      </td>
    ));

  const adHocRefusal = adHocLabel.trim().length > 0 ? refuseAdHoc?.(adHocLabel.trim()) : undefined;

  return (
    <section
      className={cn(!flush && 'overflow-hidden rounded-lg border border-border bg-card')}
      data-testid={testId ?? 'force-grid'}
      onPaste={readOnly ? undefined : onGridPaste}
    >
      <header className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-3 py-2">
        <div>
          {/* ⚠️ [D4 §6] THE PARAGRAPH BECAME A TOOLTIP. The client's one stated success
              metric is simplicity, and a paragraph above a grid reads as complexity — but
              the sentence itself is still worth having, so it moves rather than going. */}
          <Tooltip title={FORCE_COPY.gridHint}>
            <h3 className="w-fit text-sm font-semibold tracking-tight underline decoration-dotted underline-offset-4">
              Force required
            </h3>
          </Tooltip>
        </div>
        <div className="flex items-center gap-2">
          {/* ⚠️ [D4 §6] HIDDEN, NOT DELETED — `lib/surfaces.ts` says when it is reviewed.
              `splitEqually` keeps its unit test, and `surfaces.test.ts` renders BOTH
              branches so the hidden one is never unexercised. */}
          {SURFACES.forceGridSplitEqually && (
            <Tooltip title={FORCE_COPY.splitEqually}>
              <Button
                size="small"
                disabled={readOnly || grid.sources.length === 0}
                onClick={() => onChange(splitEqually(grid))}
              >
                Split equally
              </Button>
            </Tooltip>
          )}
          <Tooltip title={hasBlankSource ? FORCE_COPY.sourceNeedsDivision : undefined}>
            <Button
              size="small"
              icon={<Plus className="size-3.5" />}
              disabled={readOnly || hasBlankSource}
              onClick={addSource}
              data-testid="add-source"
              type="primary"
            >
              Add source
            </Button>
          </Tooltip>
          {/*
            ⚠️ FR-5.2.6c — A ONE-OFF ROW, FOR THIS ORDER ONLY. It never writes back to the
            policy: creating a genuinely new post type stays an admin action, and a column
            invented on one order must not quietly become part of every future one.
          */}
          {onAddAdHoc && (
            <Tooltip title={FORCE_COPY.adHocColumn}>
              <Button size="small" disabled={readOnly} onClick={() => setAdHocOpen(true)} data-testid="add-ad-hoc">
                Add column
              </Button>
            </Tooltip>
          )}
        </div>
      </header>

      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <caption className="sr-only">
            Force required by post type, and where each division is asked to send it from.
          </caption>

          {/*
            ⚠️ [FIX-1] TWO HEADER ROWS, AND THE SECOND ONE IS THE BUG FIX.

            This was ONE row: per-column headers for the POST columns, then a single
            `<th colSpan={vehicleColumns.length}>Vehicles</th>`, then the action spacer. So
            every vehicle column had a body cell full of numbers and NO header naming it —
            with one vehicle the group label sat directly above it and read like a header,
            and with two there would have been two columns of numbers under one word.

            The reported symptom ("a column renders without its label") was real; the
            diagnosis was not what it looked like. It is not a missing `shortName`, not a
            truncation, and not the one-off-label path — `requirementColumns` resolves every
            vehicle correctly and `toGrid` carries the label through. The header row simply
            never rendered it.

            `rowSpan={2}` on the columns that are not grouped, `scope="colgroup"` on the
            group cell, and `scope="col"` on the individual ones is the standard shape for a
            grouped header and is what lets a screen reader announce "Any, Vehicles".

            ⚠️ [amended C2a §4f] RESTORED. `fc1a786` deleted this block and kept the two rows,
            so the code stayed and the reason for it did not — and `ForceGrid.test.tsx`'s
            cell-count invariant then went red with nothing on hand to explain the shape it
            was measuring. The claim is now also a test; see `[FIX-1]` there.
          */}
          <thead>
            <tr className={cn(grid.vehicleColumns.length === 0 && 'border-b border-border')}>
              <th
                scope="col"
                rowSpan={2}
                className="w-56 px-2 py-2 text-left align-bottom text-xs font-medium text-muted-foreground"
              >
                Source
              </th>

              {/* Officers group */}
              {grid.postColumns.length > 0 && (
                <th
                  scope="colgroup"
                  className="border-l border-border px-1 pt-2 text-center text-[10px] font-medium uppercase tracking-wide text-subtle"
                  colSpan={grid.postColumns.length}
                >
                  Officers
                </th>
              )}

              {/* Vehicles group */}
              {grid.vehicleColumns.length > 0 && (
                <th
                  scope="colgroup"
                  className="border-l border-border px-1 pt-2 text-center text-[10px] font-medium uppercase tracking-wide text-subtle"
                  colSpan={grid.vehicleColumns.length}
                >
                  Vehicles
                </th>
              )}

              {/* [C5c §2] Shared group — the columns another duty point answers for. */}
              {coveredColumns.length > 0 && (
                <th
                  scope="colgroup"
                  className="border-l border-border px-1 pt-2 text-center text-[10px] font-medium uppercase tracking-wide text-subtle"
                  colSpan={coveredColumns.length}
                  data-testid="covered-group"
                >
                  {FORCE_COPY.sharedGroup}
                </th>
              )}

              <th scope="col" rowSpan={2} className="w-10" />
            </tr>

            {/* Second header row */}
            <tr className="border-b border-border">
              {grid.postColumns.map((column) => columnHeader(column))}
              {grid.vehicleColumns.map((column) => columnHeader(column))}
              {coveredColumns.map((column, at) => coveredHeader(column, at))}
            </tr>
          </thead>

          {/*
            ─────────────────────────────────────────────────────────────────────────
             ⚠️ [D4.4 §3] THREE BANDS: DEMAND · SUPPLY · BALANCE.

             The total row and the source rows read as the same kind of thing and they are
             OPPOSITES — the total is what the order needs, the sources are where it comes
             from, and the whole interaction is reconciling one against the other. Until
             D4.4 the only difference between them was font weight and a dashed-versus-solid
             hairline, so nothing said which was which.

             ⚠️ THE TWO WASHES ARE `hover` AND `active`, AND THE CHOICE IS MEASURED RATHER
             THAN AESTHETIC. `accent`, `muted` and `secondary` in `theme/tokens.ts` ALL
             resolve to the same `hover` value in both themes, so picking two of those names
             would have produced two bands that look identical while reading as though they
             were different. `active` is the one neutral wash that is genuinely a step
             stronger, so demand takes `secondary` (= `hover`) and balance takes `active`.
             ⚠️ NO NEW TOKEN WAS ADDED. `tokens.ts` is still the only file with a colour and
             it did not need a new one — the ramp already had the step.

             Supply sits between them untinted, on the card's own surface, indented, and is
             the only band with no rule of its own. So the reading order is: washed, plain,
             washed-darker — legible by position and weight before any label is read.

             ⚠️ A `<tbody>` PER BAND — the grouping is structural rather than a class on
             each row, so a row added to a band later cannot forget to join it.

             ⚠️ [amended C2a §4f] RESTORED. `fc1a786` deleted this while leaving `bg-secondary`
             and `bg-active` in place, so the one measured finding here — that `accent`,
             `muted` and `secondary` are the SAME VALUE and only `active` is a genuine step —
             survived nowhere, and the next reader reaching for a "second wash" would have
             produced two identical bands that read in source as though they differed.
            ─────────────────────────────────────────────────────────────────────────
          */}

          <tbody className="bg-secondary">
            <tr className="border-b-2 border-border-strong">
              <th scope="row" className="px-2 py-1.5 text-left text-sm font-bold tracking-wide">
                TOTAL REQUIRED
              </th>
              {grid.columns.map((column, columnIndex) => (
                <td
                  key={column.lineIndex}
                  className={cn('px-1 py-1', columnIndex === grid.postColumns.length && 'border-l border-border')}
                >
                  <InputNumber
                    aria-label={named(`Total required, ${column.label}`)}
                    data-testid={`total-${String(column.lineIndex)}`}
                    className="w-full"
                    size="small"
                    min={0}
                    // See the header: AntD binds the arrows to stepping, and this grid
                    // needs them to move between cells.
                    keyboard={false}
                    disabled={readOnly}
                    value={grid.totals[column.lineIndex] ?? 0}
                    onChange={(value) => setTotal(column.lineIndex, value)}
                    onKeyDown={onGridKeyDown(-1, columnIndex)}
                    ref={(node) => registerCell(`-1:${String(columnIndex)}`, node?.nativeElement ?? null)}
                  />
                </td>
              ))}
              {coveredCells('', 'px-1 py-1')}
              <td />
            </tr>
          </tbody>

          {/* ── SUPPLY. Where the force comes from — a group beneath the demand. ──── */}
          <tbody>
            {grid.sources.map((source, sourceIndex) => (
              <tr key={sourceIndex} className="border-b border-dashed border-border">
                <th scope="row" className="w-56 py-1 pl-5 pr-2 text-left font-normal">
                  <SourceCell
                    divisionOrgUnitId={source.divisionOrgUnitId}
                    unitOrgUnitId={source.unitOrgUnitId}
                    orgUnits={orgUnits}
                    onChangeDivision={(value) => setDivision(sourceIndex, value)}
                    onChangeUnit={(value) => setUnit(sourceIndex, value)}
                    disabled={readOnly}
                  />
                </th>
                {grid.columns.map((column, columnIndex) => (
                  <td
                    key={column.lineIndex}
                    className={cn('px-1 py-1', columnIndex === grid.postColumns.length && 'border-l border-border')}
                  >
                    <InputNumber
                      aria-label={named(
                        `${orgUnits.get(source.divisionOrgUnitId)?.name ?? 'New source'}, ${column.label}`,
                      )}
                      data-testid={`cell-${String(sourceIndex)}-${String(column.lineIndex)}`}
                      className="w-full"
                      size="small"
                      min={0}
                      keyboard={false}
                      disabled={readOnly}
                      value={grid.cells[sourceIndex]?.[column.lineIndex] ?? 0}
                      onChange={(value) => setCell(sourceIndex, column.lineIndex, value)}
                      onKeyDown={onGridKeyDown(sourceIndex, columnIndex)}
                      ref={(node) =>
                        registerCell(`${String(sourceIndex)}:${String(columnIndex)}`, node?.nativeElement ?? null)
                      }
                    />
                  </td>
                ))}
                {coveredCells(`cell-${String(sourceIndex)}-`, 'px-1 py-1')}

                <td className="px-1 text-center">
                  <Button
                    type="text"
                    size="small"
                    disabled={readOnly}
                    aria-label={named(`Remove ${orgUnits.get(source.divisionOrgUnitId)?.name ?? 'this source'}`)}
                    data-testid={`remove-source-${String(sourceIndex)}`}
                    icon={<Trash2 className="size-3.5" />}
                    onClick={() => removeSource(sourceIndex)}
                  />
                </td>
              </tr>
            ))}
          </tbody>

          {/* ⚠️ [D4 §6] A DARK BAND AND BOLD TEXT. It is the number an operator scans
              for, and it was previously indistinguishable from the source rows above it.
              `bg-active` is a TOKEN — `tokens.ts` is the only file with a colour.

              ⚠️ [D4.4 §3] `bg-secondary` → `bg-active`. D4 §6 asked for a DARK band and
              `secondary` is aliased to `hover`, the lightest wash in the ramp — which was
              fine while it was the only tinted row and stopped being fine the moment the
              demand band above took a wash too. `active` is the next step up and is the
              only other neutral wash that is not an alias of `hover`.

              ⚠️ [amended C2a §4f] RESTORED, with its sibling above. */}
          <tfoot>
            <tr
              className="border-t-2 border-border-strong bg-active font-semibold"
              aria-live="polite"
              data-testid="force-grid-remaining"
            >
              <th scope="row" className="px-2 py-2 text-left text-sm font-semibold">
                <Tooltip title={FORCE_COPY.remaining}>
                  <span className="underline decoration-dotted underline-offset-4">Still to source</span>
                </Tooltip>
              </th>
              {grid.columns.map((column, columnIndex) => {
                const total = grid.totals[column.lineIndex] ?? 0;
                const value = remaining[column.lineIndex] ?? 0;
                const { tone, tint } = BALANCE[balanceTone(total, value)];
                return (
                  <td
                    key={column.lineIndex}
                    data-tone={tone}
                    data-testid={`remaining-${String(column.lineIndex)}`}
                    className={cn(
                      'px-1 py-2 text-center text-sm tabular-nums',
                      /* ⚠️ [D4.5 §1e] THE WASH. `empty` contributes '' — a column nobody has
                         asked for anything in stays untinted, and keeps its `—`. */
                      tint,
                      columnIndex === grid.postColumns.length && 'border-l border-border',
                    )}
                    // The one place a colour is applied, and it is a VARIABLE reference,
                    // so a `ThemeScope` renders it correctly and no hex appears here.
                    style={{ color: TONE_VAR[tone] }}
                  >
                    {total === 0 ? <span className="text-subtle">—</span> : remainingLabel(value)}
                  </td>
                );
              })}
              {coveredCells('remaining-', 'px-1 py-2 text-sm')}
              <td />
            </tr>
          </tfoot>
        </table>
      </div>

      {/* ⚠️ [C5c §2] Not on a card whose only force is shared — there is nothing here to source. */}
      {grid.sources.length === 0 && !(grid.columns.length === 0 && coveredColumns.length > 0) && (
        <p className="border-t border-border px-3 py-2 text-xs text-muted-foreground">{FORCE_COPY.noSourcesYet}</p>
      )}

      <Modal
        open={adHocOpen}
        title="Add a one-off column"
        okText="Add"
        okButtonProps={{ disabled: adHocLabel.trim().length === 0 || adHocRefusal !== undefined }}
        onCancel={() => {
          setAdHocOpen(false);
          setAdHocLabel('');
        }}
        onOk={() => {
          onAddAdHoc?.(adHocLabel.trim());
          setAdHocOpen(false);
          setAdHocLabel('');
        }}
      >
        <p className="mb-2 text-sm text-muted-foreground">{FORCE_COPY.adHocColumn}</p>
        <Input
          autoFocus
          maxLength={80}
          placeholder="For example, Bomb squad"
          aria-label="Column name"
          value={adHocLabel}
          onChange={(event) => setAdHocLabel(event.target.value)}
        />
        {adHocRefusal !== undefined && (
          <p className="mt-2 text-sm text-destructive" role="alert" data-testid="ad-hoc-covered">
            {adHocRefusal}
          </p>
        )}
      </Modal>

      <Modal
        open={restricting !== null}
        title={FORCE_COPY.restrictAdHocTitle}
        okText="Save"
        onCancel={() => {
          setRestricting(null);
          setRestriction([]);
        }}
        onOk={() => {
          if (restricting) onRestrictAdHoc?.(restricting.lineIndex, restriction);
          setRestricting(null);
          setRestriction([]);
        }}
      >
        <p className="mb-2 text-sm text-muted-foreground">{FORCE_COPY.restrictAdHoc}</p>
        {/*
          ⚠️ EMPTY IS "ANY", AND THE PLACEHOLDER SAYS SO RATHER THAN A CHECKBOX SAYING IT.
          A "restrict this column" toggle would be a second way to express the same state, and
          the two disagree the moment somebody clears the list without unticking it.
        */}
        <Select
          mode="multiple"
          className="w-full"
          placeholder={FORCE_COPY.restrictAdHocAny}
          aria-label={FORCE_COPY.restrictAdHocTitle}
          data-testid="restrict-ad-hoc-select"
          value={restriction}
          onChange={(next: string[]) => setRestriction(next)}
          options={postTypeOptions.map((postType) => ({ value: postType.id, label: postType.name }))}
        />
      </Modal>
    </section>
  );
};
